/**
 * La raíz sin código: pantalla de club, no mapa vacío (ADR-011).
 *
 * Durante la Era Pionera GU arranca sin un solo lugar. Un desconocido que
 * llegue aquí no vería un producto que evaluar: vería un mapa en cero sin
 * explicación, que es la peor primera pantalla posible.
 *
 * **Pide el pase en vez de negarlo.** Quien llega conoce a alguien de
 * dentro —así llegó el enlace—, así que esta es la única visita del
 * producto con un canal de conversión garantizado. Gastarla en un mensaje
 * de rechazo sería tirar demanda real.
 *
 * Sin fecha de apertura y sin lista de espera: ADR-007 §8.1 prohíbe fijar
 * una fecha, y una lista abriría una segunda puerta que auditar.
 */

import { useEffect } from 'react';

import { trackEvent } from '../../shared/lib/api.js';

export interface ClubWallProps {
  /** Para quien ya entró antes y solo perdió la sesión. */
  onHasAccount?: (() => void) | undefined;
  /**
   * La segunda puerta (ADR-021). Antes de este ADR, quien llegaba sin código
   * solo podía pedirle el pase a un conocido; si no conocía a nadie, la
   * visita se perdía. Ahora hay una vía propia.
   */
  onPedirEntrar?: (() => void) | undefined;
}

const MENSAJE =
  'Oye, vi Gourmet Urbano y quiero entrar. ¿Te sobra un pase?';

export function ClubWall({ onHasAccount, onPedirEntrar }: ClubWallProps) {
  useEffect(() => {
    // Junto con `invite_wall_request_sent`, el único termómetro de demanda
    // latente que existe — y el dato con el que se decide si subir el
    // techo de 5 pases (ADR-011).
    void trackEvent('invite_wall_viewed');
  }, []);

  return (
    <main
      style={{
        minHeight: '100dvh',
        background: 'var(--gu-surface-app)',
        padding: 'var(--gu-space-8) var(--gu-space-5)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 'var(--gu-space-4)',
        maxWidth: '32rem',
        marginInline: 'auto',
      }}
    >
      <h1
        style={{
          fontSize: 'var(--gu-text-h1)',
          lineHeight: 'var(--gu-leading-tight)',
          margin: 0,
        }}
      >
        Gourmet Urbano es por invitación.
        <br />
        Todavía.
      </h1>

      <p style={{ margin: 0, color: 'var(--gu-text-secondary)' }}>
        Cada quien tiene unos cuantos pases, y nadie entra sin uno.
      </p>

      <p style={{ margin: 0, color: 'var(--gu-text-secondary)' }}>
        ¿Quién te mandó este link? <strong>Pídeselo.</strong> Le toma un toque.
      </p>

      <p style={{ margin: 0, color: 'var(--gu-text-secondary)' }}>
        ¿No conoces a nadie adentro? Cuéntanos de ti y lo vemos.
      </p>

      <a
        href={`https://wa.me/?text=${encodeURIComponent(MENSAJE)}`}
        onClick={() => void trackEvent('invite_wall_request_sent')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'var(--gu-touch-comfortable)',
          borderRadius: 'var(--gu-radius-md)',
          background: 'var(--gu-primary-strong)',
          color: 'var(--gu-white)',
          textDecoration: 'none',
          fontWeight: 550,
        }}
      >
        Pedir un pase por WhatsApp
      </a>

      {/*
       * La segunda puerta, con el MISMO peso visual que la primera.
       *
       * ADR-021: esa igualdad es lo que evita que la vía de solicitud se
       * sienta como la puerta de servicio. Si una fuera un botón y la otra
       * un enlace de texto, el copy diría una cosa y el diseño otra.
       */}
      {onPedirEntrar && (
        <button
          type="button"
          onClick={onPedirEntrar}
          style={{
            minHeight: 'var(--gu-touch-comfortable)',
            borderRadius: 'var(--gu-radius-md)',
            background: 'transparent',
            color: 'var(--gu-primary-strong)',
            border: '1.5px solid var(--gu-primary-strong)',
            fontSize: 'var(--gu-text-body)',
            fontWeight: 550,
            cursor: 'pointer',
          }}
        >
          Pedir entrar
        </button>
      )}

      {/*
       * El enlace de WhatsApp deja que el desconocido elija el contacto en
       * su propio teléfono: GU no recoge ningún dato suyo.
       */}
      <YaTengoCuenta onClick={onHasAccount} />
    </main>
  );
}

/** "Ya tengo cuenta" — secundario de verdad, no un segundo CTA. */
function YaTengoCuenta({ onClick }: { onClick?: (() => void) | undefined }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        color: 'var(--gu-text-link)',
        fontSize: 'var(--gu-text-body)',
        padding: 'var(--gu-space-3)',
        cursor: 'pointer',
        minHeight: 'var(--gu-touch-min)',
      }}
    >
      Ya tengo cuenta
    </button>
  );
}
